import Analytics from '@opencare/analytics';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Conditional } from 'common/components/Conditional/Conditional';
import { Authenticate } from 'connect/saga/handlers/user/authenticate';
import { CheckOtp } from 'connect/saga/handlers/user/otp/check-otp';

import { FormError } from 'pages/checkout/components/common/input-styled-components';

import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';

import { useAnalyticsService } from 'services/AnalyticsService';
import { useEffectOnce as useEffectOnce } from 'utils/customHooks/useEffectOnce';

import { formatErrorMessage } from 'utils/errorMessage';

import Logger from 'utils/Logger';

import { VerifyOtp } from '../../../../../../connect/saga/handlers/user/otp/verify-otp';
import { BodyText, CodeInputStateWrapper, CodeSection, InstructionsWrapper } from '../../../../components/codeForm/CodeForm-styled-components';
import { ResendCode } from '../../../../components/ResendCode/ResendCode';

import { CodeInput } from './components/CodeInput';
import { InputWrapper } from './OtpForm-styled-components';

const analytics = new Analytics();

const GENERIC_ERROR_MESSAGE = 'Something went wrong on our end, try resending the code.';
const API_MESSAGE_TO_USER_ERROR_MESSAGE = {
  'The code provided is invalid': 'The code you entered is incorrect. Please try again.',
  // Future Improvement: we should resend automatically
  'This code has expired': 'The code you entered has expired. Please request a new code.',
  // Number of attempts exceeded, cannot resend until code has expired
  'Too many verification attempts have been made. Please wait': 'Please wait before requesting a new code.',
  // Rate Limited, cannot resend until `resendAt` time
  'Too many attempts have been made. Please try again later.': 'Please wait before requesting a new code.',
  'Too many verification attempts have been made. Please try again later.': 'Please wait before requesting a new code.',
};

const OTP_LENGTH = 6;

export const OtpForm = () => {
  const dispatch = useDispatch();

  const analyticsService = useAnalyticsService();

  const codeInputRef = useRef<HTMLInputElement>(null);

  const [oneTimePin, setOneTimePin] = useState<string>('');
  const [showResendCode, setShowResendCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const { goToNextStep } = useNextCheckoutStep();

  const authenticateResponse = useSelector(Authenticate.value!.selector);
  const {
    otpSent: { phone: sentOtpToPhone, email: sentOtpToEmail },
  } = useMemo(
    () => authenticateResponse || {},
    [authenticateResponse],
  );

  const {
    error: verifyOtpError,
    loading: verifyOtpLoading,
    success: verifyOtpSuccess,
  } = useSelector(VerifyOtp.selector);

  const {
    error: checkOtpError,
    loading: checkOtpLoading,
    value: checkOtpValue,
  } = useSelector(CheckOtp.selector);

  useEffect(() => {
    const showCodeTimeout = setTimeout(
      () => setShowResendCode(true),
      5000,
    );

    return () => clearTimeout(showCodeTimeout);
  }, []);

  useEffectOnce(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-otp',
      name: 'checkout',
      section: 'otp',
      ...analyticsService.analyticsData,
    });
  });

  const sendOtpToUser = useCallback(() => {
    dispatch(VerifyOtp.reset());
    // Reset the one time pin
    setOneTimePin('');
    // CheckOtp will resend the OTP if it's not sent yet
    dispatch(CheckOtp.createAction());
  }, [dispatch]);
  // Check OTP on mount
  useEffectOnce(sendOtpToUser);

  const resendCooldownInterval = useRef<NodeJS.Timeout | null>(null);

  const resendOtpToUser = useCallback(() => {
    if (resendCooldown > 0) {
      return;
    }

    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-otp',
      name: 'checkout',
      section: 'otp',
      content: {
        action: 'resend-code',
      },
    });

    sendOtpToUser();

    if (resendCooldownInterval.current) {
      clearInterval(resendCooldownInterval.current);
    }

    const resendCodeTime =
      checkOtpValue?.resendAt
      || moment.utc().add(1, 'minute')
        .toISOString();

    const newResendCooldown = moment.utc(resendCodeTime).diff(moment.utc(), 'seconds');

    setResendCooldown(newResendCooldown);
    resendCooldownInterval.current = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    // Reset Verify state so we don't show stale error messages
    dispatch(VerifyOtp.reset());
  }, [sendOtpToUser, resendCooldown, checkOtpValue?.resendAt, dispatch]);

  const rawErrorMessage = useMemo(
    // Check OTP should take precedence since if it errors we can't verify the OTP
    () => checkOtpError || verifyOtpError,
    [checkOtpError, verifyOtpError],
  );

  const errorMessage = useMemo(() => {
    if (!rawErrorMessage) {
      return null;
    }

    // Special edge case - user is logged in, move them to the next step
    if (rawErrorMessage.includes('You must log out first to perform this action')) {
      Logger.error('User is already logged in on OTP form, moving to next step');
      return goToNextStep();
    }

    return formatErrorMessage({
      errorMessage: rawErrorMessage,
      defaultErrorMessage: GENERIC_ERROR_MESSAGE,
      errorMessageMap: API_MESSAGE_TO_USER_ERROR_MESSAGE,
    });
  }, [rawErrorMessage, goToNextStep]);

  // Handle error state
  useEffect(() => {
    if (!rawErrorMessage || verifyOtpLoading) {
      return;
    }

    setShowResendCode(true);
    setOneTimePin('');

    codeInputRef.current?.focus();
  }, [rawErrorMessage, verifyOtpLoading]);

  const handleOtpVerifySuccess = useCallback(() => {
    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-otp',
      name: 'checkout',
      section: 'otp',
      content: {
        status: 'success',
      },
    });

    dispatch(VerifyOtp.success?.update(false));
    goToNextStep();
  }, [goToNextStep, dispatch]);

  // Handle success state
  useEffect(() => {
    if (verifyOtpLoading || rawErrorMessage || !verifyOtpSuccess) {
      return;
    }

    handleOtpVerifySuccess();
  }, [handleOtpVerifySuccess, verifyOtpLoading, verifyOtpSuccess, rawErrorMessage]);

  const verifyOtp = (code: string) => {
    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-otp',
      name: 'checkout',
      section: 'otp',
      content: {
        action: 'submit-code',
      },
    });

    dispatch(VerifyOtp.createAction({ code }));
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Only set the value if it is blank or only contains digits
    const isValueAllDigits = /^\d*$/.test(value);
    if (!isValueAllDigits) {
      return;
    }

    if (value.length <= OTP_LENGTH) {
      setOneTimePin(value);
    }

    if (value.length === OTP_LENGTH) {
      verifyOtp(value);
    }
  };

  const isLoadingOrSendingCode = useMemo(
    () => verifyOtpLoading || checkOtpLoading,
    [verifyOtpLoading, checkOtpLoading],
  );

  const deliveryMethodText = useMemo(() => {
    const methods = [
      ...(sentOtpToPhone ? ['phone'] : []),
      ...(sentOtpToEmail ? ['email'] : []),
    ];

    return methods.join(' or ');
  }, [sentOtpToPhone, sentOtpToEmail]);

  return (
    <>
      <Conditional condition={Boolean(errorMessage)}>
        <FormError>{`${errorMessage}`}</FormError>
      </Conditional>

      <CodeSection>
        <CodeInputStateWrapper>
          <InstructionsWrapper>
            <BodyText>Enter the code we sent to your {deliveryMethodText}</BodyText>
          </InstructionsWrapper>
        </CodeInputStateWrapper>

        <InputWrapper>
          <CodeInput
            ref={codeInputRef}
            maxLength={OTP_LENGTH}
            value={oneTimePin}
            onChange={handleCodeChange}
            isLoading={isLoadingOrSendingCode}
          />
        </InputWrapper>
      </CodeSection>

      <Conditional condition={showResendCode}>
        <ResendCode
          resendOtpToUser={resendOtpToUser}
          resendCooldown={resendCooldown}
          isLoading={checkOtpLoading || verifyOtpLoading}
        />
      </Conditional>
    </>
  );
};
